import React, { createContext, useContext, useState } from 'react';
import { ServiceBooking, Reservation } from '@turizi/shared';
import { useAddPersonToServiceBooking, useAddPersonToReservation } from '@/hooks/api-hooks';
import PaxSelector from '../pax-selector';
import { useTranslation } from 'react-i18next';

interface BookingPaxsAssociationContextType {
    openClientSelector: (booking: ServiceBooking, reservation?: Reservation) => void;
}

const BookingPaxsAssociationContext = createContext<BookingPaxsAssociationContextType | undefined>(undefined);

export function useBookingPaxsAssociation() {
    const context = useContext(BookingPaxsAssociationContext);
    if (!context) {
        throw new Error('useBookingPaxsAssociation must be used within a BookingPaxsAssociationProvider');
    }
    return context;
}

export function BookingPaxsAssociationProvider({ children }: { children: React.ReactNode }) {
    const { t } = useTranslation();
    const [activeBooking, setActiveBooking] = useState<ServiceBooking | null>(null);
    const [activeReservation, setActiveReservation] = useState<Reservation | null>(null);

    const { mutate: addPersonBooking } = useAddPersonToServiceBooking();
    const { mutate: addPersonReservation } = useAddPersonToReservation();

    const openClientSelector = (booking: ServiceBooking, reservation?: Reservation) => {
        setActiveBooking(booking);
        setActiveReservation(reservation || null);
    };

    const closeClientSelector = () => {
        setActiveBooking(null);
        setActiveReservation(null);
    };

    const handleSelect = (person: any) => {
        if (!activeBooking) return;
        const bookingId = activeBooking.id;
        const reservationId = activeReservation?.id || activeBooking.reservation_id;

        if (reservationId) {
            addPersonReservation({ reservationId, personId: person.id });
        }
        addPersonBooking({ serviceBookingId: bookingId, personId: person.id });
        closeClientSelector();
    };

    return (
        <BookingPaxsAssociationContext.Provider value={{ openClientSelector }}>
            {children}
            {activeBooking && (
                <PaxSelector
                    variant='unstyled'
                    autoFocus
                    onSelect={handleSelect}
                    onClose={closeClientSelector}
                />
            )}
        </BookingPaxsAssociationContext.Provider>
    );
}
